import { getCurrentScope, onScopeDispose, readonly, ref, type DeepReadonly, type Ref } from 'vue';
import { LARGE_MOBILE_MAX_WIDTH_PX, PHONE_MAX_WIDTH_PX, isDockedBarTier, type LayoutTier } from '@/lib/layoutTier';
import { log } from '@/lib/log';

// Mobile & foldable redesign (docs/plans/mobile-foldable-redesign.md §2.1) —
// the reactive half of the one layout-tier resolver. `lib/layoutTier.ts` owns
// the pure classification; this owns the browser subscription.
//
// MODULE-LEVEL SINGLETON on purpose. `App.vue` (rail vs tab bar, dock
// geometry), `Live.vue` (which stage composition), `Guide.vue` (agenda vs
// timeline) and `EpgGrid.vue` (docked-player occlusion) all ask the same
// question, and two independent `matchMedia` subscriptions can report
// different answers within one tick during a resize — which is exactly the
// class of bug the shell used to have when three files each wrote their own
// breakpoint literal. One ref, one pair of listeners, one value per tick.
//
// `matchMedia` rather than a resize listener: the browser already owns the
// boundary evaluation, it fires only on a crossing (not on every resize
// pixel), and it uses the same engine the CSS half of the resolver uses — so
// the JS and CSS tiers cannot disagree about what "640px wide" means.

/** `(min-width: 640px)` — true for large-mobile and desktop. */
export const LARGE_MOBILE_QUERY = `(min-width: ${PHONE_MAX_WIDTH_PX + 1}px)`;
/** `(min-width: 1024px)` — true for desktop only. */
export const DESKTOP_QUERY = `(min-width: ${LARGE_MOBILE_MAX_WIDTH_PX + 1}px)`;

// SSR-safe default: with no window to measure, assume the layout that expects
// the most room. `tierForWidth` degrades a non-finite width the same way.
const tier = ref<LayoutTier>('desktop');

let largeMobileMql: MediaQueryList | null = null;
let desktopMql: MediaQueryList | null = null;
let refCount = 0;
// Set when a consumer registers outside a reactive scope (a plain module, a
// test helper). Such a consumer has no unmount to hook, so tearing the
// listeners down when the *component* consumers all leave would silently
// freeze its value. Once sticky, we keep listening for the page's lifetime —
// two matchMedia listeners is not a leak worth a stale layout.
let sticky = false;

function recompute(): void {
    if (desktopMql?.matches) {
        tier.value = 'desktop';
        return;
    }
    if (largeMobileMql?.matches) {
        tier.value = 'large-mobile';
        return;
    }
    tier.value = 'phone';
}

function attach(): void {
    if (largeMobileMql || typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
        return;
    }
    largeMobileMql = window.matchMedia(LARGE_MOBILE_QUERY);
    desktopMql = window.matchMedia(DESKTOP_QUERY);
    if (typeof largeMobileMql.addEventListener === 'function') {
        largeMobileMql.addEventListener('change', recompute);
        desktopMql.addEventListener('change', recompute);
    } else {
        // A `MediaQueryList` without `addEventListener` (Safari < 14 exposed
        // only the deprecated `addListener`) would otherwise freeze the tier at
        // whatever it was when the first consumer mounted — a silent wrong
        // layout for the rest of the session. Say so rather than hiding it
        // behind an optional call that "handles" nothing.
        log.warn('layoutTier.matchmedia_listener_unsupported', { query: LARGE_MOBILE_QUERY });
    }
    recompute();
}

function detach(): void {
    largeMobileMql?.removeEventListener?.('change', recompute);
    desktopMql?.removeEventListener?.('change', recompute);
    largeMobileMql = null;
    desktopMql = null;
}

/**
 * The current layout tier, reactive across viewport changes.
 *
 * Safe to call during `setup()` — the value is correct on the first read, so
 * a `computed` that gates the rail against the tab bar does not render one
 * frame of the wrong shell.
 */
export function useLayoutTier(): {
    tier: DeepReadonly<Ref<LayoutTier>>;
    isPhone: () => boolean;
    isDesktop: () => boolean;
    isDockedBar: () => boolean;
} {
    attach();
    if (getCurrentScope()) {
        refCount++;
        onScopeDispose(() => {
            refCount--;
            if (refCount <= 0) {
                refCount = 0;
                if (!sticky) detach();
            }
        });
    } else {
        sticky = true;
    }
    return {
        tier: readonly(tier),
        isPhone: () => tier.value === 'phone',
        isDesktop: () => tier.value === 'desktop',
        isDockedBar: () => isDockedBarTier(tier.value),
    };
}

/** Test-only — drops the listeners and resets the shared value. */
export function _resetLayoutTierForTests(): void {
    detach();
    refCount = 0;
    sticky = false;
    tier.value = 'desktop';
}
